import { useNavigate } from "react-router-dom";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { hasManagingAdminRole } from "@/lib/auth/admin-roles";

import { MoreHorizontal } from "lucide-react";
import { useToast } from "@/shared/rd";
import { ActionMenu, type ActionMenuItem } from "../rd-local";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import type { SubscriptionPlanType } from "@/lib/api/types";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { StorePaymentModal } from "./StorePaymentModal";
import { StorePaymentsModal } from "./StorePaymentsModal";
import { StoreScheduleLedgerModal } from "./StoreScheduleLedgerModal";
import { StoreExtendTrialModal } from "./StoreExtendTrialModal";
import { useImpersonateStore } from "@/shared/services/useImpersonateStore";
import { invalidateAdminStoreSubscriptions } from "@/lib/query/core/invalidation";
import { useSetStorePremiumMutation } from "../../services/mutations/useSetStorePremiumMutation";
import { describeTierError } from "./tierErrors";

type Props = {
  storeId: string;
  locale: string;
  isFreeTrial?: boolean;
  isPremium?: boolean;
  /** Drives the top-up wording: a per-order store funds a wallet, not a schedule. */
  planType?: SubscriptionPlanType;
  tier?: "FREE" | "PREMIUM" | "COMPLIMENTARY" | "LEGACY";
};

export function StoreSubscriptionActionsMenu({
  storeId,
  locale,
  isFreeTrial,
  isPremium,
  planType,
  tier,
}: Props) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { data: session } = useSessionQuery();
  const canManage = hasManagingAdminRole(session?.admin?.roles);
  const isDefault =
    tier === "FREE" ||
    tier === "PREMIUM" ||
    tier === "COMPLIMENTARY" ||
    planType === "FREE" ||
    planType === "PREMIUM";
  const isPerOrderCredit =
    isDefault || planType === SUBSCRIPTION_PLAN_TYPE.PER_ORDER_CREDIT;
  const queryClient = useQueryClient();
  const [isLedgerOpen, setIsLedgerOpen] = useState(false);
  const [isPaymentsOpen, setIsPaymentsOpen] = useState(false);
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const [isExtendTrialOpen, setIsExtendTrialOpen] = useState(false);
  const premiumMutation = useSetStorePremiumMutation();
  const impersonation = useImpersonateStore(storeId, locale);

  const [premiumError, setPremiumError] = useState<unknown>(null);
  const toast = useToast();
  const setPremium = async (next: boolean, undoing = false) => {
    // The menu closes itself on select, so there is no panel state to clear.
    setPremiumError(null);
    try {
      await premiumMutation.mutateAsync({ storeId, isPremium: next });
      // The same endpoint turns it back, so the answer offers Undo.
      toast.show({
        tone: "neutral",
        message: t(next ? "admin.premiumOnToast" : "admin.premiumOffToast"),
        ...(undoing ? {} : { actionLabel: t("admin.undo"), onAction: () => void setPremium(!next, true) }),
      });
    } catch (failure) {
      // Never swallowed: the admin must see that the toggle did not apply.
      setPremiumError(failure ?? new Error("Premium toggle failed"));
    }
  };

  const entries: ActionMenuItem[] = [
    {
      key: "impersonate",
      label: impersonation.isSubmitting
        ? t(keys.admin.impersonateSellerPending)
        : t(keys.admin.impersonateSellerAction),
      disabled: impersonation.isSubmitting,
      onSelect: () => void impersonation.impersonate(),
    },
    {
      key: "ledger",
      label: t(keys.admin.storeSubscriptionsScheduleLedgerAction),
      onSelect: () => setIsLedgerOpen(true),
    },
    {
      key: "payments",
      label: t(keys.admin.storeSubscriptionsPaymentsAction),
      onSelect: () => setIsPaymentsOpen(true),
    },
    ...(!canManage || isFreeTrial
      ? []
      : [
          {
            key: "add-payment",
            label: isPerOrderCredit
              ? t(keys.admin.subscriptionWalletTopUpAction)
              : t(keys.admin.subscriptionPaymentAddAction),
            onSelect: () => setIsAddPaymentOpen(true),
          },
        ]),
    ...(isDefault
      ? [
          {
            key: "tier",
            label: t(keys.admin.tierManage),
            onSelect: () => navigate(`/${locale}/admin/stores/${storeId}`),
          },
        ]
      : canManage
        ? [
            // Available for paid plans too — the backend snapshots the current plan
            // into a fresh free-trial assignment for the granted days.
            {
              key: "extend-trial",
              label: t(keys.admin.storeSubscriptionsExtendTrialAction),
              onSelect: () => setIsExtendTrialOpen(true),
            },
            {
              key: "premium",
              label: isPremium
                ? t(keys.admin.storeSubscriptionsPremiumDisableAction)
                : t(keys.admin.storeSubscriptionsPremiumEnableAction),
              disabled: premiumMutation.isPending,
              onSelect: () => void setPremium(!isPremium),
            },
          ]
        : []),
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
      <ActionMenu label={t(keys.admin.storeSubscriptionsActions)} items={entries} icon={MoreHorizontal} />
      {premiumError ? (
        <span style={{ fontSize: 12, color: "var(--rd-red)" }} role="alert">
          {describeTierError(premiumError, t)}
        </span>
      ) : null}
      {impersonation.error ? (
        <span style={{ fontSize: 12, color: "var(--rd-red)" }} role="alert">
          {impersonation.error}
        </span>
      ) : null}

      <StoreScheduleLedgerModal
        storeId={storeId}
        locale={locale}
        isOpen={isLedgerOpen}
        onOpenChange={setIsLedgerOpen}
        hideTrigger
      />
      <StorePaymentsModal
        storeId={storeId}
        locale={locale}
        isOpen={isPaymentsOpen}
        onOpenChange={setIsPaymentsOpen}
        hideTrigger
      />
      {!canManage || isFreeTrial ? null : (
        <StorePaymentModal
          storeId={storeId}
          planType={planType}
          isOpen={isAddPaymentOpen}
          onOpenChange={setIsAddPaymentOpen}
          hideTrigger
          onSuccess={() => {
            void invalidateAdminStoreSubscriptions(queryClient, storeId);
          }}
        />
      )}
      {!isDefault && canManage ? (
        <StoreExtendTrialModal
          storeId={storeId}
          isOpen={isExtendTrialOpen}
          onOpenChange={setIsExtendTrialOpen}
        />
      ) : null}
    </div>
  );
}
